'use client';

import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';

/**
 * Renders `stackWrapped` everywhere except /embed/* routes, where it renders
 * `plain` instead.
 *
 * /embed/v1/chart and /embed/v1/billing are deliberately public,
 * token-authenticated iframe widgets (next.config.mjs's embedCsp allows
 * framing from any origin, frame-ancestors *) and never call any Stack Auth
 * hook — see app/embed/**. But @stackframe/stack's SDK kicks off an
 * unconditional background fetch to
 * https://api.stack-auth.com/api/v1/internal/backend-urls the moment
 * StackProvider mounts (fetchBackendUrlsInBackground in
 * @stackframe/stack's dist/lib/stack-app/apps/implementations/common.js),
 * regardless of whether anything in the tree reads auth state. The embed
 * CSP's connect-src is intentionally narrower than the main app's (no
 * https://*.stack-auth.com) — a widget embeddable by any origin shouldn't be
 * able to reach our own auth provider — so that fetch was correctly blocked,
 * producing a CSP-violation console error (and a Stack SDK `captureError`)
 * on every real customer's embedded widget, on every single page load.
 *
 * `stackWrapped` and `plain` are built SERVER-SIDE in app/layout.tsx and
 * passed down as pre-rendered React children, NOT as the raw
 * `stackServerApp` instance — a class instance can't cross the RSC
 * client/server boundary as a plain prop ("Only plain objects ... can be
 * passed to Client Components from Server Components. Classes ... not
 * supported"). Passing the already-built <StackProvider> element as opaque
 * children means React only ever mounts (and @stackframe/stack's SDK only
 * ever runs) the branch actually selected here.
 */
export function StackProviderGate({
  stackWrapped,
  plain,
}: {
  stackWrapped: ReactNode;
  plain: ReactNode;
}) {
  const pathname = usePathname();
  const isEmbedRoute = pathname === '/embed' || pathname?.startsWith('/embed/');
  return isEmbedRoute ? plain : stackWrapped;
}
